
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface StepOption {
  id: string;
  icon: string;
  title: string;
  description: string;
}

interface Step {
  id: number;
  category: string;
  question: string;
  subtitle: string;
  type: 'select' | 'multi-select' | 'form';
  options?: StepOption[];
}

const steps: Step[] = [
  {
    id: 1,
    category: 'PROJECT TYPE',
    question: 'What type of branding do you need?',
    subtitle: 'Choose the option that best describes your project.',
    type: 'select',
    options: [
      { id: 'type-new', icon: 'ri-sparkling-line', title: 'New Brand Identity', description: 'Starting fresh with a complete brand from scratch.' },
      { id: 'type-rebrand', icon: 'ri-refresh-line', title: 'Rebrand / Refresh', description: 'Modernize or completely transform your existing brand.' },
      { id: 'type-logo', icon: 'ri-pencil-ruler-2-line', title: 'Logo Design Only', description: 'Just need a professional logo for your business.' },
      { id: 'type-guidelines', icon: 'ri-book-3-line', title: 'Brand Guidelines', description: 'Document and standardize your existing brand.' },
      { id: 'type-extension', icon: 'ri-git-branch-line', title: 'Brand Extension', description: 'Expand your brand for a new product or sub-brand.' },
      { id: 'type-packaging', icon: 'ri-gift-line', title: 'Packaging Design', description: 'Product packaging that stands out on shelves.' },
    ],
  },
  {
    id: 2,
    category: 'DELIVERABLES',
    question: 'What deliverables do you need?',
    subtitle: 'Select all that apply to your project.',
    type: 'multi-select',
    options: [
      { id: 'del-logo', icon: 'ri-shape-line', title: 'Logo & Variations', description: 'Primary logo with alternate versions and icons.' },
      { id: 'del-colors', icon: 'ri-palette-line', title: 'Color Palette', description: 'Strategic color system with specifications.' },
      { id: 'del-typography', icon: 'ri-font-size-2', title: 'Typography System', description: 'Font selections and usage guidelines.' },
      { id: 'del-guidelines', icon: 'ri-file-list-3-line', title: 'Brand Guidelines PDF', description: 'Comprehensive brand usage manual.' },
      { id: 'del-stationery', icon: 'ri-mail-open-line', title: 'Business Stationery', description: 'Business cards, letterheads, envelopes.' },
      { id: 'del-social', icon: 'ri-instagram-line', title: 'Social Media Kit', description: 'Templates and assets for social platforms.' },
    ],
  },
  {
    id: 3,
    category: 'YOUR BUDGET',
    question: 'What is your estimated budget?',
    subtitle: 'This helps us recommend the right package for you.',
    type: 'select',
    options: [
      { id: 'budget-starter', icon: 'ri-seedling-line', title: 'Under $2,000', description: 'Great for startups needing essential branding.' },
      { id: 'budget-mid', icon: 'ri-plant-line', title: '$2,000 – $5,000', description: 'Ideal for growing businesses wanting a complete identity.' },
      { id: 'budget-premium', icon: 'ri-tree-line', title: '$5,000 – $10,000', description: 'For comprehensive brand systems with all deliverables.' },
      { id: 'budget-enterprise', icon: 'ri-building-2-line', title: '$10,000+', description: 'Enterprise-level branding with strategy and research.' },
      { id: 'budget-unsure', icon: 'ri-question-line', title: 'Not Sure Yet', description: 'I\'d like guidance on what fits my needs and goals.' },
    ],
  },
  {
    id: 4,
    category: 'TIMELINE',
    question: 'When do you need your branding ready?',
    subtitle: 'Let us know your ideal delivery timeline.',
    type: 'select',
    options: [
      { id: 'time-asap', icon: 'ri-flashlight-line', title: 'ASAP (2–3 weeks)', description: 'I need it delivered as soon as possible.' },
      { id: 'time-month', icon: 'ri-calendar-line', title: '1–2 Months', description: 'A comfortable timeline for quality work.' },
      { id: 'time-quarter', icon: 'ri-calendar-2-line', title: '3+ Months', description: 'No rush — I want to get everything perfect.' },
      { id: 'time-flexible', icon: 'ri-time-line', title: 'Flexible', description: 'I\'m open to your recommendation on timing.' },
    ],
  },
  {
    id: 5,
    category: 'YOUR DETAILS',
    question: 'Almost there! Tell us about yourself.',
    subtitle: 'We\'ll use this to get back to you with a tailored proposal.',
    type: 'form',
  },
];

export default function BrandingEnquiryForm() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [answers, setAnswers] = useState<Record<number, string | string[]>>({});
  const [formData, setFormData] = useState({ name: '', email: '', company: '', phone: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const totalSteps = steps.length;
  const progress = (currentStep / totalSteps) * 100;
  const currentStepData = steps[currentStep - 1];

  const handleSelect = (optionId: string) => {
    if (currentStepData.type === 'multi-select') {
      const current = (answers[currentStep] as string[]) || [];
      if (current.includes(optionId)) {
        setAnswers({ ...answers, [currentStep]: current.filter((id) => id !== optionId) });
      } else {
        setAnswers({ ...answers, [currentStep]: [...current, optionId] });
      }
    } else {
      setAnswers({ ...answers, [currentStep]: optionId });
    }
  };

  const isOptionSelected = (optionId: string) => {
    const answer = answers[currentStep];
    if (Array.isArray(answer)) return answer.includes(optionId);
    return answer === optionId;
  };

  const canProceed = () => {
    if (currentStepData.type === 'form') return true;
    const answer = answers[currentStep];
    if (Array.isArray(answer)) return answer.length > 0;
    return !!answer;
  };

  const handleNext = () => {
    if (currentStep < totalSteps) setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'Please enter a valid email';
    if (formData.message.length > 500) errors.message = 'Message must be under 500 characters';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const getAnswerLabels = () => {
    const labels: Record<string, string> = {};
    steps.forEach((step) => {
      if (step.type === 'form') return;
      const answer = answers[step.id];
      if (!answer) return;
      if (Array.isArray(answer)) {
        const selected = step.options?.filter((o) => answer.includes(o.id)).map((o) => o.title) || [];
        labels[step.category] = selected.join(', ');
      } else {
        const option = step.options?.find((o) => o.id === answer);
        if (option) labels[step.category] = option.title;
      }
    });
    return labels;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const answerLabels = getAnswerLabels();
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('email', formData.email);
      body.append('company', formData.company);
      body.append('phone', formData.phone);
      body.append('message', formData.message);
      Object.entries(answerLabels).forEach(([key, value]) => {
        body.append(key, value);
      });

      await fetch('https://readdy.ai/api/form/d64rsekbd5onm6bapnjg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      navigate('/services/branding/thank-you');
    } catch {
      navigate('/services/branding/thank-you');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setCurrentStep(1);
    setAnswers({});
    setFormData({ name: '', email: '', company: '', phone: '', message: '' });
    setFormErrors({});
    setIsOpen(false);
  };

  const scrollToForm = () => {
    setIsOpen(true);
    setTimeout(() => {
      document.getElementById('branding-enquiry')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  if (!isOpen) {
    return (
      <section className="py-20 bg-slate-900" id="branding-enquiry">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00989e]/20 rounded-full mb-6">
            <i className="ri-questionnaire-line text-[#00989e]"></i>
            <span className="text-[#00b4a6] text-sm font-medium">Quick Enquiry</span>
          </div>
          <h2 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Ready to Build Your Brand?
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-10">
            Answer a few quick questions and we'll put together a tailored branding proposal just for you — no commitment required.
          </p>
          <button
            onClick={scrollToForm}
            className="inline-flex items-center gap-3 px-10 py-4 bg-[#00989e] text-white font-semibold rounded-full hover:bg-[#007a7f] transition-colors cursor-pointer whitespace-nowrap text-lg"
          >
            <i className="ri-arrow-right-circle-line text-xl"></i>
            Start Your Free Enquiry
          </button>
          <p className="text-gray-500 text-sm mt-4">Takes less than 2 minutes</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-slate-900" id="branding-enquiry">
      <div className="max-w-2xl mx-auto px-6 lg:px-8">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
              Step {currentStep} of {totalSteps}
            </span>
            <span className="text-xs font-semibold text-[#00989e]">
              {Math.round(progress)}% complete
            </span>
          </div>
          <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#00989e] to-[#00b4a6] rounded-full transition-all duration-700 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Step Header */}
        <div className="text-center mb-10">
          <span className="inline-block px-3 py-1 bg-[#00989e]/20 text-[#00b4a6] text-xs font-bold uppercase tracking-widest rounded-full mb-4">
            {currentStepData.category}
          </span>
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            {currentStepData.question}
          </h3>
          <p className="text-gray-400">{currentStepData.subtitle}</p>
        </div>

        {/* Options or Form */}
        {currentStepData.type !== 'form' ? (
          <div className="grid gap-3 mb-10">
            {currentStepData.options?.map((option) => {
              const selected = isOptionSelected(option.id);
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => handleSelect(option.id)}
                  className={`relative w-full text-left p-5 rounded-xl border-2 transition-all duration-300 cursor-pointer group hover:shadow-md ${
                    selected
                      ? 'border-[#00989e] bg-[#00989e]/10 shadow-md'
                      : 'border-slate-700 bg-slate-800 hover:border-[#00989e]/40'
                  }`}
                >
                  {selected && (
                    <div className="absolute top-3 right-3 w-6 h-6 bg-[#00989e] rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                  )}
                  {currentStepData.type === 'multi-select' && !selected && (
                    <div className="absolute top-3 right-3 w-6 h-6 border-2 border-slate-600 rounded-full" />
                  )}
                  <div className="flex items-start gap-4">
                    <div
                      className={`w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                        selected
                          ? 'bg-[#00989e] text-white'
                          : 'bg-slate-700 text-gray-400 group-hover:bg-[#00989e]/20 group-hover:text-[#00989e]'
                      }`}
                    >
                      <i className={`${option.icon} text-xl`}></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-white text-[15px] mb-1">{option.title}</h4>
                      <p className="text-gray-400 text-sm leading-relaxed">{option.description}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <form
            id="branding-enquiry-form"
            data-readdy-form
            onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}
            className="space-y-5 mb-10"
          >
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={(e) => { setFormData({ ...formData, name: e.target.value }); setFormErrors({ ...formErrors, name: '' }); }}
                  className={`w-full px-4 py-3 bg-slate-800 border rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00989e] transition-colors ${formErrors.name ? 'border-red-500' : 'border-slate-700'}`}
                  placeholder="John Smith"
                />
                {formErrors.name && <p className="text-red-400 text-xs mt-1">{formErrors.name}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => { setFormData({ ...formData, email: e.target.value }); setFormErrors({ ...formErrors, email: '' }); }}
                  className={`w-full px-4 py-3 bg-slate-800 border rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00989e] transition-colors ${formErrors.email ? 'border-red-500' : 'border-slate-700'}`}
                  placeholder="john@company.com"
                />
                {formErrors.email && <p className="text-red-400 text-xs mt-1">{formErrors.email}</p>}
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Company Name</label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00989e] transition-colors"
                  placeholder="Your Company"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00989e] transition-colors"
                  placeholder="+1 (555) 000-0000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Tell us about your brand vision</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setFormData({ ...formData, message: e.target.value });
                    setFormErrors({ ...formErrors, message: '' });
                  }
                }}
                maxLength={500}
                rows={4}
                className={`w-full px-4 py-3 bg-slate-800 border rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00989e] transition-colors resize-none ${formErrors.message ? 'border-red-500' : 'border-slate-700'}`}
                placeholder="Describe your brand vision, target audience, or any specific requirements..."
              />
              <div className="flex justify-between mt-1">
                {formErrors.message && <p className="text-red-400 text-xs">{formErrors.message}</p>}
                <span className="text-gray-500 text-xs ml-auto">{formData.message.length}/500</span>
              </div>
            </div>
          </form>
        )}

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between">
          <button
            onClick={currentStep === 1 ? resetForm : handleBack}
            className="flex items-center gap-2 px-6 py-3 text-gray-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            {currentStep === 1 ? 'Cancel' : 'Back'}
          </button>
          {currentStep < totalSteps ? (
            <button
              onClick={handleNext}
              disabled={!canProceed()}
              className={`flex items-center gap-2 px-8 py-3 font-semibold rounded-full transition-all cursor-pointer whitespace-nowrap ${
                canProceed()
                  ? 'bg-[#00989e] text-white hover:bg-[#007a7f]'
                  : 'bg-slate-700 text-slate-500 cursor-not-allowed'
              }`}
            >
              Next
              <i className="ri-arrow-right-line"></i>
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex items-center gap-2 px-8 py-3 bg-[#00989e] text-white font-semibold rounded-full hover:bg-[#007a7f] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
            >
              {isSubmitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Sending...
                </>
              ) : (
                <>
                  Submit Enquiry
                  <i className="ri-send-plane-line"></i>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
